import Component, {mixins} from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {CartMixin} from '../../../Mixins';
import Cross from '../../Icons/Cross.vue';
import Trash from '../../Icons/Trash.vue';
import Edit from '../../Icons/Edit.vue';
import Refresh from '../../Icons/Refresh.vue';
import {CLEAR_REGISTRATION_CODES, UPDATE_CART} from '../../../store/commits';
import {mapGetters} from 'vuex';
import {CustomerDataDTO} from '../../../DTOs';
import {ApiErrorHandler} from '../../../Errors/ApiErrorHandler';
import CartItem from '../CartItem/CartItem.vue';
import {PrivacyStatement, TermsAndConditions, TicketConfiguration} from '../../../Models';
import {OrderErrorCodes} from '../../../Errors/OrderErrorCodes';
import {PaymentTypeEnum, PublicLinks} from '../../../Enums';
import {PrivacyStatementService, TermsAndConditionsService} from '../../../Services';
import Modal from '../../Modal/Modal.vue';
import Vue from 'vue';

@Component({
  computed: {
    ...mapGetters({
      isGuestOrder: 'getIsGuestOrder',
      currentBuyerData: 'getBuyerData',
    }),
  },
  components: {
    Cross,
    Trash,
    Edit,
    Refresh,
    CartItem,
    Modal,
  },
})
export default class CartView extends mixins(CartMixin) {
  @Prop({required: true, type: String}) loginPageUrl: string | undefined;
  @Prop({type: String, required: true}) ticketShopUrl: string | undefined;
  @Prop({type: String, required: true}) ticketCheckoutUrl: string | undefined;
  @Prop({required: false, type: Boolean, default: true}) allowCodeInput: Boolean | undefined;
  @Prop({required: false, type: Boolean, default: false}) readonly: Boolean | undefined;

  newUserCode: string = '';
  newUserCodeError: boolean = false;
  isGuestOrder: boolean | undefined;
  currentBuyerData: CustomerDataDTO | undefined;
  checkingQuantity = false;
  newUserCodeIsPromotion: boolean = true;

  private paymentProducts: Array<any> = [];
  private privacyStatementService!: PrivacyStatementService;
  private termsAndConditionsService!: TermsAndConditionsService;
  private privacyStatements: any = {};
  private agb: any = {};
  private selectedClient: string | null = null;

  created() {
    this.privacyStatementService = new PrivacyStatementService(this.inventoryUrl);
    this.termsAndConditionsService = new TermsAndConditionsService(this.inventoryUrl);
  }

  async mounted() {
    this.$store.commit(CLEAR_REGISTRATION_CODES);
    this.setAvailablePaymentMethods();
    this.getPrivacyStatements();

    if (!this.checkoutView) {
      this.sendDataLayerEvent();
      localStorage.removeItem('dataLayerData');
    }
  }

  async redeemCode() {
    const code = this.newUserCode.toUpperCase();
    this.newUserCodeError = false;
    this.newUserCodeIsPromotion = true;
    if (this.currentOrder && !this.currentOrder.codes.includes(code) && code.length > 0) {
      const codeResult: CodeResult = await this.orderService.redeemUnclassifiedCode(this.newUserCode);
      if (codeResult.success && codeResult.code) {
        if (codeResult.code.parentName === 'promotion') {
          this.orderDTO.addCode(code);
          try {
            let cart = await this.orderService.updateCart(this.orderDTO, this.userLanguage);
            this.$store.commit(UPDATE_CART, cart);
            let success = this.currentOrder.codes.includes(code);
            this.newUserCode = '';
            if (success) {
              this.showInfoToast('Code eingelöst.', 3000);
            } else {
              this.orderDTO.removeCode(code);
              if (cart.errors[code] && cart.errors[code].includes(OrderErrorCodes.PROMOTIONCODE_NO_EFFECT)) {
                this.showInfoToast('Code hat keine Auswirkungen.', 3000);
              } else {
                this.showInfoToast('Code konnte nicht eingelöst werden.', 3000);
              }
            }
          } catch (e) {
            this.orderDTO.removeCode(code);
            (new ApiErrorHandler().handle(e));
            console.error(e);
            this.showErrorToast('Code konnte nicht eingelöst werden.');
            // rollback changes
          }
        } else {
          this.newUserCodeIsPromotion = false;
        }
      } else {
        this.showErrorToast('Code konnte nicht eingelöst werden.');
      }
    } else {
      this.newUserCodeError = true;
    }
  }

  async checkOrderQuantity() {
    if (this.loggedIn && this.currentOrder && this.currentBuyerData && !this.isGuestOrder) {
      this.checkingQuantity = true;
      try {
        const result = await this.orderService.checkQuantity(this.currentOrder.id, this.currentBuyerData.email);
        if (result.valid === true && this.loginPageUrl) {
          window.location.href = this.loginPageUrl;
        }
      } catch (e) {
        const handler = new ApiErrorHandler();
        const code = handler.getErrorCode(e);
        const data = handler.getErrorData(e);
        if (code && data && data.invalidProducts.length > 0) {
          this.showErrorToast(<string>this.$t('toasts.improperAmountOfProduct', {
            code: code,
            descriptionExternal: data.invalidProducts[0].product.descriptionExternal,
          }));
        } else if (code) {
          this.showErrorToast(<string>this.$t('toasts.invalidShoppingCart', {code: code}));
        }
        this.checkingQuantity = false;
      }
    }
  }

  async setAvailablePaymentMethods() {
    const paymentMethods = this.paymentMethods;
    // Get only ids-array from active payment-methods
    let activePaymentMethodIds = paymentMethods.map((pm, key) => pm.id);
    // If paymentMethods are empty for the event, all paymentmethods are allowed, so set the active paymentmethods
    let tickets = this.ticketConfigurations;
    tickets.forEach(function (ticket) {
      if (!ticket.event.paymentMethods || ticket.event.paymentMethods.length === 0) {
        // @ts-ignore
        ticket.event.paymentMethods = activePaymentMethodIds;
      }
    });

    tickets.forEach((ticket) => {
      // Create an intersection for N sets of payment-method-ids
      const intersectedEventPaymentMethodsIds = ticket.event.paymentMethods.filter(value => ticket.event.paymentMethods.includes(value));
      // Create intersection from event-paymentmethods and active paymentmethods
      // Just in case an inactive paymentmethod is set for an event
      const intersection = intersectedEventPaymentMethodsIds.filter(value => activePaymentMethodIds.includes(value));

      // Return payment-methods form intersectionId-Array
      // @ts-ignore
      let availablePaymentMethods = paymentMethods.filter(pm => intersection.indexOf(pm.id) !== -1);
      if (availablePaymentMethods.length > 0) {
        const availablePayments: Array<string> = [];
        // Display all paymentMethods that are active and set available in events (lowest common denominator)
        availablePaymentMethods.forEach((pm) => {
          availablePayments.push(pm.type);
        });
        this.paymentProducts.push({eventId: ticket.event.id, paymentMethods: availablePayments});
      } else {
        // Fallback to credit-card if there are no available payment methods
        this.paymentProducts.push({eventId: ticket.event.id, paymentMethods: PaymentTypeEnum.creditcard.value});
      }
    });
  }

  async getPrivacyStatements() {
    const clientIds = this.events.map(e => e.clientId);
    const statements = await this.privacyStatementService.listCached<PrivacyStatement>('', {clientId: `[${clientIds.join(',')}]`});
    const termsAndConditions = await this.termsAndConditionsService.listCached<TermsAndConditions>(
      '',
      {clientId: `[${clientIds.join(',')}]`},
    );
    this.events.forEach(event => {
      const ps = statements.find(p => p.clientId === event.clientId);
      const tac = termsAndConditions.find(p => p.clientId === event.clientId);
      if (ps) {
        Vue.set(this.privacyStatements, `${event.clientId}`, ps);
      }
      if (tac) {
        Vue.set(this.agb, `${event.clientId}`, tac);
      }
    });
  }

  setConfigQuantity(ticketConfiguration: TicketConfiguration, value: number) {
    if (ticketConfiguration) {
      ticketConfiguration.quantity = value;
    }
  }

  setSubConfigQuantity(ticketConfiguration: TicketConfiguration, value: number) {
    if (ticketConfiguration) {
      ticketConfiguration.quantity = value;
    }
  }

  openPrivacyModal(client) {
    this.selectedClient = client;
    // @ts-ignore
    this.$refs.privacyModal.showModal();
  }

  openAGBModal(client) {
    this.selectedClient = client;
    // @ts-ignore
    this.$refs.agbModal.showModal();
  }

  sendDataLayerEvent(): void {
    if (this.cart) {
      const value = this.cart.sum.toFixed(2);
      const items: object[] = [];
      this.ticketConfigurations.forEach((item) => {
        const ticketName = item.product.descriptionWebShop;
        const ticketId = String(item.product.id);
        const ticketPrice = item.promotion?.price.toFixed(2) ?? item.product.price.toFixed(2);
        const currency = item.product.currency === '€' ? 'EUR' : item.product.currency;
        const fairName = item.event.descriptionExternal;

        const client = this.clients.find(c => c.id === item.event.clientId) || null;
        const organizer = client ? `${client.title}` : '';

        const item_category2 = '';
        const item_category3 = '';
        const item_category4 = '';

        const advertising = this.cart!.advertised?.find(a => a.productId === item.product.id);
        const actionCode = advertising ? `${advertising.code}` : '';

        const promotionCode = item.promotion?.code;
        const code = actionCode || promotionCode || '';
        const codeOrganizer = actionCode ? organizer : '';
        const localPubTransIncluded = item.product.localPubTransIncluded === 1 ? 'Ja' : 'Nein';
        const item_variant = {var1: code, var2: codeOrganizer, var3: localPubTransIncluded};
        const quantity = item.quantity;

        items.push({
          item_name: ticketName,
          item_id: ticketId,
          price: ticketPrice,
          currency,
          item_brand: fairName,
          item_category: organizer,
          item_category2,
          item_category3,
          item_category4,
          item_variant,
          quantity,
        });
      });

      const data = {value, items};

      // @ts-ignore
      window.dataLayer.push({
        event: 'viewCart',
        ...data,
      });
    }
  }

  get canCheckout(): boolean {
    return this.loggedIn && this.cart !== null && this.ticketConfigurations.length > 0;
  }

  get isGuest(): boolean {
    return Boolean(this.isGuestOrder);
  }

  get loggedIn(): boolean {
    return Boolean(this.currentBuyerData);
  }

  get isTimeSlotVisible(): boolean {
    this.editableTicketConfigurations.forEach(configuration => {
      if (configuration.slot) {
        return true;
      }
    });
    return false;
  }

  get totalQuantity() {
    let total = 0;
    this.editableTicketConfigurations.forEach(c => total += c.quantity);
    return total;
  }

  get paymentChoices() {
    const choices: Array<{ value: string, text: string, disabled: boolean }> = [];
    if (this.ticketConfigurations.length > 0) {
      this.ticketConfigurations.forEach((ticket) => {
        this.paymentProducts.forEach((payment) => {
          if (payment.eventId === ticket.event.id) {
            try {
              payment.paymentMethods.forEach((p) => {
                if (PaymentTypeEnum[p]) {
                  choices.push({
                    value: PaymentTypeEnum[p].value,
                    text: PaymentTypeEnum[p].title[this.userLanguage],
                    disabled: false,
                  });
                }
              });
            } catch {
              if (PaymentTypeEnum[payment]) {
                choices.push({
                  value: PaymentTypeEnum[payment].value,
                  text: PaymentTypeEnum[payment].title[this.userLanguage],
                  disabled: false,
                });
              }
            }
          }
        });
      });
    }
    // only display distinct payment methods
    const choiceValues: string[] = [];
    return choices.filter((choice, index) => {
      if (!choiceValues.includes(choice.value)) {
        choiceValues.push(choice.value);
        return choice;
      }
    });
  }

  get allPrivacyStatements() {
    const privacyStatements: any = [];
    this.ticketConfigurations.forEach((ticket) => {
      privacyStatements[ticket.event.clientId] = this.privacyStatements[ticket.event.clientId] || null;
    });
    return privacyStatements;
  }

  get allAGBs() {
    const agb: any = [];
    this.ticketConfigurations.forEach((ticket) => {
      agb[ticket.event.clientId] = this.agb[ticket.event.clientId] || null;
    });
    return agb;
  }

  get i18nTermsAndConditionsLink(): string {
    return PublicLinks.TERMS_AND_CONDITIONS[this.userLanguage];
  }

  get i18nPrivacyStatementLink(): string {
    return PublicLinks.PRIVACY_STATEMENT[this.userLanguage];
  }

  get i18nTermsAndConditionsContent(): string {
    if (this.selectedClient && this.allAGBs[this.selectedClient]) {
      let content = this.allAGBs[this.selectedClient].html;
      if (this.userLanguage !== 'de' && this.allAGBs[this.selectedClient].translations) {
        const translation = this.allAGBs[this.selectedClient].translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  get i18nPrivacyStatementContent(): string {
    if (this.selectedClient && this.allPrivacyStatements[this.selectedClient]) {
      let content = this.allPrivacyStatements[this.selectedClient].html;
      if (this.userLanguage !== 'de' && this.allPrivacyStatements[this.selectedClient].translations) {
        const translation = this.allPrivacyStatements[this.selectedClient].translations[this.userLanguage];
        content = translation ? translation.html : content;
      }
      return content;
    }
    return '';
  }

  /* reset error when newUserCode updates */
  @Watch('newUserCode')
  setNewUserCode(newValue: string, oldValue: string) {
    if (this.readonly) {
      return;
    }
    if (oldValue !== newValue) {
      this.newUserCode = newValue.toUpperCase();
      this.newUserCodeError = false;
      this.newUserCodeIsPromotion = true;
    }
  }

  get amountEventsInCart(): number {
    return Object.keys(this.computedEditableTicketConfigurationsByEvent).length;
  }
}
